import * as React from "react";
import {CC_EMAIL_ID, COLOR, INPUT_TYPE} from "utils/variables";
import {dateFormat} from "userform/utils";
import {diffYears, getDaysInMillSec, unflatten} from "utils";
import {IDocument, IProfile} from "StateInterface";
import { FIELD_NAMES } from "profile/enums";
import BasicCard from "myshubhlife-component/card";
import { ILoanApplicationInputField } from "loan-application/interface";
import { getCompanyInfo } from "./apiCall";
import {DocTypes} from "myshubhlife-component/docInput";

export enum RequiredFields {
    PERSONAL_DETAILS = "Personal Details",
    BASIC_INFO = "Basic Information",
    EDUCATION_AND_EMPLOYMENT = "Education and Employment",
    REFERENCES = "References",
    BANK_DETAILS = "Bank Details",
    LOAN_DETAILS = "Loan Details",
    DOCUMENT_UPLOAD = "Document Upload",
    KYC = "KYC",
}

export enum DocUploadModes {
    SCAN_UPLOAD = "SCAN_UPLOAD",
    OKYC = "OKYC",
}
const fieldErrors = {
    workExperienceMonths: "Please enter your total work experience in numbers. Ex: 18",
    netMonthlyIncome: "Please enter your net monthly income in numbers. Ex: 18000",
    pincode: "Please enter the pincode. Ex: 560025",
};

const getDocumentsName = (docName: string) => {
    return docName.split("_").join(" ").toLowerCase();
};

export const getCustomizedRegexError = (prop: ILoanApplicationInputField) => {
    const splitter = (prop.identifier || prop.name).split(":");
    const identifier = splitter[splitter.length - 1];
    if (fieldErrors[identifier]) {
       return fieldErrors[identifier];
    } else {
        return `Please enter correct ${prop.label.toLowerCase()} to continue the application`;
    }

};

const checkMinMaxVal = (property) => {
  return property.type === INPUT_TYPE.DATE ? ((diffYears(new Date(), new Date(property.value)) < property.min_val) ||
  (diffYears(new Date(), new Date(property.value)) > property.max_val)) : (property.value < property.min_val || property.value > property.max_val)
}

export const getCustomizedErrors = (property) => {
    const type = property.type || property.inputType;
    return `Please ${type === INPUT_TYPE.DATE || type === INPUT_TYPE.DROPDOWN || type === INPUT_TYPE.RADIO ? `select ${property.label.toLowerCase()}` : `enter ${property.label.toLowerCase()}`}  ${property.min_val && property.max_val ? `between ${property.min_val} and ${property.max_val}` : property.min_val ? `minimum ${property.min_val}` : `${property.max_val ? `maximum ${property.max_val}` : "to continue the application"}`}`;
};

export const monthsToDate = (months: number) => {
    const currentDate = new Date();
    const dateInMili = currentDate.setMonth(currentDate.getMonth() - months);
    return dateInMili;
};

export const handleFormValidation = (fields, applicationFields, documents, profileDetails?: IProfile) => {
    let totalWorkExperience = profileDetails && profileDetails?.workExperienceMonths && monthsToDate(profileDetails.workExperienceMonths);
    let workingSince = null;
    for (const fieldCategoryName of Object.keys(applicationFields)) {
        for (const property of applicationFields[fieldCategoryName]) {
            const identifier = property.identifier;
            if (identifier === FIELD_NAMES.WORK_EXPERIENCE_MONTHS) {
                totalWorkExperience = identifier in fields ?
                    monthsToDate(fields[identifier].value) : monthsToDate(property.value);
            } else if (identifier === FIELD_NAMES.DURATION) {
                workingSince = identifier in fields ?
                    getValueForUserInput(fields[identifier].value, property.type) : property.value;
            }
            if (property.type === INPUT_TYPE.FILE) {
                if (documents.filter((item) => item.type === identifier).length === 0) {
                    return {
                        status: false,
                        errors: {[identifier]: `Please upload your ${getDocumentsName(identifier)} to continue the application`},
                    };
                }
            } else if (fields.hasOwnProperty(identifier) ||
                property.value === undefined ||
                property.value === null ||
                (property.value && property.value.length === 0)) {
                if (!fields.hasOwnProperty(identifier) || fields[identifier].value.length === 0 ||
                  checkMinMaxVal({...property, ...fields[identifier]})
                ) {
                    return {
                        status: false,
                        errors: {[identifier]: getCustomizedErrors(property)},
                    };
                } else if (property.regex &&
                    !new RegExp(property.regex).test(fields[identifier].value)) {
                    return {
                        status: false,
                        errors: {[identifier]: getCustomizedRegexError(property)},
                    };
                } else if (property.type === INPUT_TYPE.DATE &&
                    (new Date(fields[identifier].value) > new Date() || (workingSince && totalWorkExperience && totalWorkExperience > workingSince))) {
                    return {
                        status: false,
                        errors: {[identifier]: `Please select ${property.label.toLowerCase()} lesser than ${property.identifier === FIELD_NAMES.DURATION ? "total work experience" : "current date"} `},
                    };
                }
            } else if (property.regex &&
                !new RegExp(property.regex).test(property.value)) {
                return {
                    status: false,
                    errors: {[identifier]: getCustomizedRegexError(property)},
                };
             } else if ((property.min_val || property.max_val) && checkMinMaxVal(property)) {
                return {
                    status: false,
                    errors: {[identifier]: getCustomizedErrors(property)},
                };
             } else if (totalWorkExperience && workingSince && totalWorkExperience > workingSince ) {
                return {
                    status: false,
                    errors: {duration: "Please select work experience less then total experience"},
                };
            }
        }
    }
    return {
        status: true,
        errors: null,
    };
};
// Todo
export const handleCategoryValidation = (currentValues) => {
    let validation = {status: true, errors: null};
    for (const property of currentValues) {
        if (!property.value && property.value !== 0) {
            validation = {status: false, errors: {[property.name]: "Please enter mandatory details"}};
            break;
        } else if (property.regex !== undefined && property.regex !== null &&
            !new RegExp(property.regex).test(property.value)) {
            validation = {
                status: false,
                errors: {[property.name]: "Please enter correct details"},
            };
            break;
        } else if (property.inputType === INPUT_TYPE.DATE &&
            (diffYears(new Date(), new Date(property.value)) < property.min ||
                diffYears(new Date(), new Date(property.value)) > property.max)
        ) {
            validation = {
                status: false,
                errors: {[property.name]: `Age should be between ${property.min} and ${property.max}`},
            };
            break;
        }
    }
    return validation;
};

export const eligibilityScreenFields = (profile?: IProfile, disabledKeys?: object) => [
            {
                label: "Pan Number",
                inputType: INPUT_TYPE.TEXT,
                name: FIELD_NAMES.PAN,
                value: profile ? profile.panNumber : "",
                disabled: false,
                regex: "^([a-zA-Z]){5}([0-9]){4}([a-zA-Z]){1}?$",
            },
            {
                label: "Date of Birth",
                inputType: INPUT_TYPE.DATE,
                min: 18,
                max: 60,
                name: FIELD_NAMES.DOB,
                value: profile ? getProfileValueForInputField(profile.dob , INPUT_TYPE.DATE) : "",
                disabled: false,
            },
            {
                label: "Pincode",
                inputType: INPUT_TYPE.NUMBER,
                name: FIELD_NAMES.PINCODE,
                value: profile && profile["alternateAddresses"] && profile.alternateAddresses.length ? profile.alternateAddresses[profile.alternateAddresses.length - 1].pincode : "",
                max_length: 6,
                disabled: false,
                regex: "^[0-9]{6}$",
            },
            {
                label: "Employment Type",
                inputType: INPUT_TYPE.RADIO,
                name: FIELD_NAMES.EMPLOYMENT_TYPE,
                value: profile && profile.financeBasicInfo ? profile.financeBasicInfo.employmentType : "",
                options: [{
                    name: "SELF EMPLOYED",
                    value: "SELF_EMPLOYED",
                },
                    {
                        name: "SALARIED",
                        value: "SALARIED",
                    }],
                disabled: disabledKeys ? disabledKeys[FIELD_NAMES.EMPLOYMENT_TYPE] : false,
            },
            {
                label: "Net Monthly Income",
                inputType: INPUT_TYPE.NUMBER,
                max_length: 10,
                name: FIELD_NAMES.NET_MONTHLY_INCOME,
                value: profile && profile.financeBasicInfo ? profile.financeBasicInfo.netMonthlyIncome : "",
                disabled: false,
                regex: "^[0-9]+$",
            },
        ];

export function inputFieldStyles() {
    return {
        handleContainerStyle: (hasError) => {
            return {
                padding: ".5rem",
                margin: "3px",
                fontSize: "15px",
                color: COLOR.GRAY,
                fontWeight: "500",
            };
        },
        headerStyle: {
            color: COLOR.BLACK,
            fontSize: "18px",
            padding: "0 0px 10px 10px",
            marginBottom: ".5rem",
            fontWeight: 600,
            marginTop: ".8rem",
        },
        submitButtonStyle: {
            backgroundColor: COLOR.PRIMARY,
            cursor: "pointer",
            color: COLOR.WHITE,
            border: "none",
            padding: "10px",
            width: "96%",
            margin: "4% 2%",
            borderRadius: "25px",
        },
        inputStyle: {
            handleTextFieldStyle: (hasError) => {
                return {
                    color: "#495057",
                    border: "none",
                    borderBottom: `1px solid ${hasError ? "red" : "lightgray"}`,
                    padding: ".375rem 0",
                    width: "100%",
                    fontWeight: "600",
                };
            },
            handleDropdownStyle: (hasError) => {
                return {
                    color: "#495057",
                    border: "none",
                    background: "transparent",
                    borderBottom: `1px solid ${hasError ? "red" : "lightgray"}`,
                    padding: ".375rem 0",
                    width: "100%",
                    fontWeight: "600",
                };
            },
            handleRadioStyle: (hasError) => {
                return {
                    background: COLOR.WHITE,
                    border: "1px solid #e6e7e8",
                    minWidth: "80px",
                    color: COLOR.GRAY,
                    padding: "5px",
                    marginTop: ".3rem",
                    marginRight: ".2rem",
                    display: "inline-block",
                    borderRadius: ".2rem",
                    width: "49%",
                    onSelect: {
                        background: COLOR.PRIMARY,
                        border: "1px solid #F5821F",
                        color: COLOR.WHITE,
                    },
                };
            },
            handleUploadDocStyle: (hasError) => {
                return {
                    borderRadius: "5px",
                    color: "rgb(33 33 33)",
                    border: `1px solid ${hasError ? "red" : "#f5d6b6"}`,
                    padding: "15px",
                    width: "100%",
                    fontSize: "15px",
                };
            },
            handleDateStyle: (hasError) => {
                return {
                    color: "#495057",
                    border: "none",
                    background: "transparent",
                    borderBottom: `1px solid ${hasError ? "red" : "lightgray"}`,
                    padding: ".375rem 0",
                    width: "100%",
                    fontWeight: "600",
                };
            },
        },
        handleErrorStyle: (hasError) => {
            return {
                color: "red",
                font: "12px",
            };
        },
    };
}

export function getValueForUserInput(value, type: string) {
    if (type === INPUT_TYPE.DATE) {
        return value ? new Date(value).getTime() : null;
    }
    return value;
}

export function getProfileValueForInputField(value, type: string) {
    if (type === "date") {
        return value === "" ? null : dateFormat(value);
    }
    return value;
}

export const eligibilityFieldsKey = [
    "dob",
    "addressPincode",
    "employmentType",
    "salaryMode",
    "workExperienceMonths",
    "currentEmployer",
    "workCin",
];

export const getRequiredFieldsKey = (obj: object) => {
    const keys = [];
    Object.keys(obj).forEach((title) => {
        keys.push(title);
    });
    return keys;
};

export const defaultErrorMessage = (msg: string) => {
    return (
        <BasicCard>
             <div className="error-message-container">
                <div style={{color: COLOR.RED}}>{msg}<br/>
                    <small>
                        <a href={`mailto: ${CC_EMAIL_ID}`} style={{color: COLOR.PRIMARY}}>
                            Mail us at: {CC_EMAIL_ID}
                        </a>
                    </small>
                </div>
             </div>
        </BasicCard>
    );
};

export const categoriesOrder = [
    RequiredFields.PERSONAL_DETAILS,
    RequiredFields.EDUCATION_AND_EMPLOYMENT,
    RequiredFields.REFERENCES,
    RequiredFields.BANK_DETAILS,
    RequiredFields.LOAN_DETAILS,
    RequiredFields.DOCUMENT_UPLOAD,
    RequiredFields.KYC,
];

export const makeCatOrder = (orderedCategoryList: string[], data: object) => {
    const reqField = data["required_fields"];
    const applicationFields = {
        "required_fields": {},
        "optional_fields": {},
    };
    for (const category in reqField) {
        if (orderedCategoryList.includes(category)) {
            orderedCategoryList.map((orderedCategory, idx) => {
                if (category !== orderedCategory && reqField[orderedCategory]) {
                    applicationFields.required_fields[orderedCategory] = reqField[orderedCategory];
                } else {
                    applicationFields.required_fields[category] = reqField[category];
                }
            });
        } else {
            applicationFields.required_fields[category] = reqField[category];
        }
    }
    return applicationFields;
};

export const paramToProfile = (authExt: {[key: string]: string}, profileData: IProfile) => {
    enum FieldIndentifiers {
        emp_id = "employeeId",
        name = "name",
        panNumber = "panNumber",
        mobile = "mobile",
        gender = "gender",
        dob = "dob",
        fatherName = "fatherOrSpouseName",
        workExperience = "workExperienceMonths",
        employmentType = "financeBasicInfo:employmentType",
        salaryMode = "financeBasicInfo:salaryMode",
        monthlyIncome = "financeBasicInfo:netMonthlyIncome",
        beneficiaryName = "bankAccounts:0:beneficiaryName",
        accountNumber = "bankAccounts:0:accountNumber",
        ifsc = "bankAccounts:0:ifsc",
        designation = "designation",
        employerType = "employerType",
        residentType = "residentType",
        residentTenure = "resTenure",
        relationshipStatus = "relationshipStatus",
        duration = "duration",
        education = "education",
        loanTenure = "loanRequest:tenure",
        loanAmount = "loanRequest:amount",
        loanPurpose = "loanRequest:purpose",
        workPincode = "workAddress:pincode",
        workHouse = "workAddress:house",
        workLocality = "workAddress:locality",
        referenceName1 = "references:0:referenceName",
        referenceName2 = "references:1:referenceName",
        referenceMobile1 = "references:0:referenceMobile",
        referenceMobile2 = "references:1:referenceMobile",
        addressHouse = "alternateAddresses:0:house",
        addressStreet = "alternateAddresses:0:street",
        addressLocality = "alternateAddresses:0:locality",
        addressLandmark = "alternateAddresses:0:landmark",
        addressPincode = "alternateAddresses:0:pincode",
    }
    const formatedData = [
        FieldIndentifiers.employmentType,
        FieldIndentifiers.salaryMode,
        FieldIndentifiers.employerType,
    ];
    const dateFields = [
        FieldIndentifiers.dob,
        FieldIndentifiers.duration,
        FieldIndentifiers.residentTenure,
    ];
    const selectOption = {
        [FieldIndentifiers.residentType]: ["Rented", "Leased", "PG", "CompanyHouse", "OwnHouse"],
        [FieldIndentifiers.gender]: ["male", "female", "transgender"],
        [FieldIndentifiers.employmentType]: ["SALARIED", "SELF_EMPLOYED"],
        [FieldIndentifiers.salaryMode]: ["BANK_TRANSFER", "CHEQUE", "CASH"],
        [FieldIndentifiers.employerType]: ["PARTNERSHIP", "LLP", "PRIVATE_LIMITED", "PUBLIC_LIMITED", "MNC", "PROPRIETORSHIP", "GOVERNMENT", "TRUSTS", "OTHERS"],
        [FieldIndentifiers.education]: ["Class 7 or below", "Class 8 to Class 11", "Class 12 or Diploma", "Graduate or Post Graduate"],
        [FieldIndentifiers.loanPurpose]: ["Rental Deposit", "Medical", "Electronics", "Marriage", "Repay old loan", "Education", "Vehicle Purchase", "New Business", "Other"],
    };
    const profile = {};
    for (const key in authExt) {
        if (FieldIndentifiers.hasOwnProperty(key)) {
            authExt[key] =  !formatedData.includes(FieldIndentifiers[key]) ?
              authExt[key]?.replace(/%20/g, " ")?.split("_").join(" ") : authExt[key].toUpperCase();
            if (dateFields.includes(FieldIndentifiers[key])) {
              authExt[key] = String(new Date(authExt[key]).getTime());
            } else if (selectOption[FieldIndentifiers[key]]) {
                authExt[key] = selectOption[FieldIndentifiers[key]][selectOption[FieldIndentifiers[key]].
                  map((f: string) => f.toLowerCase()).indexOf(authExt[key].toLowerCase())];
            }
            profile[FieldIndentifiers[key]] = authExt[key];
        }
    }
    return unflatten(profile, profileData);
};

export const prefillMarkEmail = [
    "test.shubhloans@gmail.com",
    "test.myshubhlife@gmail.com",
    "test.myshubhlife1@gmail.com",
    "myshubhlife.test123@gmail.com",
];

const docPrefillCalculation = (docs: IDocument, latestStatusUpdateDate: number) => {
    const daysInMillSec = getDaysInMillSec(30);
    const currentTime = new Date().getTime();
    const lastDocUploadTime = new Date(docs.date).getTime();
    return !latestStatusUpdateDate ?
        (lastDocUploadTime > (currentTime - daysInMillSec)) :
        (lastDocUploadTime > (currentTime - daysInMillSec)) && lastDocUploadTime > latestStatusUpdateDate;
};

export const getPrefillDocs = (docs: IDocument[], latestStatusUpdateDate: number) => {
    const prefillDocs = [];
    docs.forEach((item) => {
        if (docPrefillCalculation(item, latestStatusUpdateDate)) {
            prefillDocs.push({type: item.type, date: item.date});
        }
    });
    return prefillDocs;
};

export const getLoanApplicationDocs = `${DocTypes.CURRENT_ADDRESS_PROOF},${DocTypes.SELFIE},${DocTypes.PAN}`;
